import { Button } from "../../components/ui/button";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "../../components/ui/tooltip";
import { TooltipWrapper } from "../../components/ui/tooltip-wrapper";
import { Logger } from "@/lib/logger/Logger";
import { formatLocalDate } from "@/lib/utils/format-local-date";
import { useCentralStore } from "../../store/Central";
import { getClientDisplayName } from "@/lib/utils/client-utils";
import { AbstractInvoiceTable } from "./components/abstract-invoice-table.component";
import { useAccountingStore } from "../../store/Accounting";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { SelectionRange } from "@progress/kendo-react-dateinputs";
import { CustomDateRangePicker } from "../../components/custom-date-range-picker/custom-date-range-picker.component";
import { AccountingRecipientSelector } from "./components/accounting-recipient-selector.component";
import { useSearchParams } from "react-router-dom";
import { showNotification } from "../../store/Central/selectors";
import { AccountingZakDialog } from "./components/accounting-zak-dialog.component";
import { isConfigTrue } from "../organization/subpages/administration/konfigurationseinstellungen.page";
import { TableExportDialog } from "./components/table-export-dialog.component";
import { PaymentDialog } from "./components/payment-dialog.component";
import { ExportDataResult, useTableExport } from "../../hooks/useTableExport";
import {
	EXPORT_MAX_ROWS,
	ExportHeaderMetadata,
} from "../../lib/utils/export-utils";
import { SortingState, PaginationState } from "@tanstack/react-table";
import { Search, Info, X } from "lucide-react";
import { InvoiceCancellationDialog } from "./components/invoice-cancellation-dialog.component";
import {
	TableInvoiceRow,
	PaymentDetail,
	TableRowData,
	ProcessedTransaction,
} from "./types/invoice-table.types";
import { Camt54Upload } from "./components/camt54-upload.component";
import { ProcessingResultsDialog } from "./components/processing-results-dialog.component";
import { useInvoiceReminderActions } from "./mahnwesen/hooks/useInvoiceReminderActions";

export const FakturajournalPage: React.FC = () => {
	const { organization, clientsLookup } = useCentralStore((state) => ({
		organization: state.organization,
		clientsLookup: state.clientsLookup,
	}));
	const organizationId = organization?.id;
	const [loading, setLoading] = useState<boolean>(true);
	const [searchParams, setSearchParams] = useSearchParams();
	const [selectedRecipientId, setSelectedRecipientId] = useState<string>(
		searchParams.get("recipient") || ""
	);
	const initialSearch = searchParams.get("search") || "";
	const [zakSentTransactions, setZakSentTransactions] = useState<
		ProcessedTransaction[]
	>([]);
	const [zakResultsDialogOpen, setZakResultsDialogOpen] =
		useState<boolean>(false);
	const [zakDialogOpen, setZakDialogOpen] = useState<boolean>(false);
	const [paymentDialogOpen, setPaymentDialogOpen] = useState<boolean>(false);
	const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] =
		useState<TableInvoiceRow | null>(null);
	const [cancellationDialogOpen, setCancellationDialogOpen] =
		useState<boolean>(false);
	const [selectedInvoiceForCancellation, setSelectedInvoiceForCancellation] =
		useState<TableInvoiceRow | null>(null);
	const [cancellationLoading, setCancellationLoading] =
		useState<boolean>(false);
	const [dateValue, setDateValue] = useState<SelectionRange>({
		start: null,
		end: null,
	});

	// Table state for TanStack Table
	const [sorting, setSorting] = useState<SortingState>([
		{ id: "invoice_doc_number", desc: true },
	]);
	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: 100,
	});
	const [globalFilter, setGlobalFilter] = useState<string>(initialSearch);
	const [totalRowCount, setTotalRowCount] = useState<number>(0);
	const requestIdRef = useRef(0); //necessary to prevent race conditions when fetching data

	const {
		invoicePaymentsData,
		fetchInvoicePaymentsData,
		recipientsLookup,
		cancelInvoice,
		createPayment,
	} = useAccountingStore((state) => ({
		invoicePaymentsData: state.invoicePaymentsData,
		fetchInvoicePaymentsData: state.fetchInvoicePaymentsData,
		recipientsLookup: state.recipientsLookup,
		cancelInvoice: state.cancelInvoice,
		createPayment: state.createPayment,
	}));

	const augmentedRecipientsLookup = useMemo(() => {
		if (
			!selectedRecipientId ||
			selectedRecipientId === "ALL" ||
			recipientsLookup[selectedRecipientId]
		) {
			return recipientsLookup;
		}
		const client = clientsLookup[selectedRecipientId];
		if (!client) return recipientsLookup;
		return {
			...recipientsLookup,
			[selectedRecipientId]: {
				type: "client" as const,
				name: getClientDisplayName(client, true),
			},
		};
	}, [recipientsLookup, selectedRecipientId, clientsLookup]);

	const handleFetchAccountingData = useCallback(async () => {
		if (!organizationId) {
			Logger.error("No organization id found");
			return;
		}

		const currentRequestId = ++requestIdRef.current;
		setLoading(true);

		try {
			const sortBy = sorting[0]?.id || "invoice_doc_number";
			const sortDirection = sorting[0]?.desc ? "desc" : "asc";

			const result = await fetchInvoicePaymentsData(
				dateValue.start ? formatLocalDate(dateValue.start) : "",
				dateValue.end ? formatLocalDate(dateValue.end) : "",
				selectedRecipientId,
				{
					status: "active",
					sortBy,
					sortDirection,
					pageIndex: pagination.pageIndex,
					pageSize: pagination.pageSize,
					globalFilter: globalFilter,
					updateStore: true,
				}
			);

			if (requestIdRef.current === currentRequestId) {
				if (result) {
					setTotalRowCount(result.totalCount);
					Logger.log("Server response:", {
						dataLength: result.data.length,
						totalCount: result.totalCount,
						currentPage: pagination.pageIndex,
						pageSize: pagination.pageSize,
					});
				} else {
					Logger.error("No result from fetchInvoicePaymentsData");
					setTotalRowCount(0);
				}
			}
		} finally {
			if (requestIdRef.current === currentRequestId) {
				setLoading(false);
			}
		}
	}, [
		organizationId,
		fetchInvoicePaymentsData,
		dateValue.start,
		dateValue.end,
		selectedRecipientId,
		sorting,
		pagination,
		globalFilter,
	]);

	const { reminderActions, reminderDialogs } = useInvoiceReminderActions(
		handleFetchAccountingData
	);

	const { exportTable } = useTableExport();

	const formatDate = (date: Date | null): string => {
		if (!date) return "";
		return date.toLocaleDateString("de-CH", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		});
	};

	const headerMetadata: ExportHeaderMetadata = useMemo(() => {
		const recipientName = selectedRecipientId
			? augmentedRecipientsLookup[selectedRecipientId]?.name
			: undefined;

		const sortingLabel = sorting[0]
			? `${
					sorting[0].id === "invoice_doc_number"
						? "Rechnungsnummer"
						: sorting[0].id === "invoice_date"
							? "Rechnungsdatum"
							: sorting[0].id
				} (${sorting[0].desc ? "absteigend" : "aufsteigend"})`
			: undefined;

		return {
			exportType: "fakturajournal" as const,
			exportTypeLabel: "Fakturajournal",
			exportDate: formatDate(new Date()),
			dateRange:
				dateValue.start && dateValue.end
					? {
							start: formatDate(dateValue.start),
							end: formatDate(dateValue.end),
						}
					: undefined,
			recipientFilter: recipientName,
			globalFilter: globalFilter || undefined,
			sorting: sortingLabel,
		};
	}, [
		dateValue.start,
		dateValue.end,
		selectedRecipientId,
		augmentedRecipientsLookup,
		sorting,
		globalFilter,
	]);

	const fetchExportData = async (): Promise<ExportDataResult> => {
		const currentSortBy = sorting[0]?.id || "invoice_doc_number";
		const currentSortDirection = sorting[0]?.desc ? "desc" : "asc";

		return fetchInvoicePaymentsData(
			dateValue.start ? formatLocalDate(dateValue.start) : "",
			dateValue.end ? formatLocalDate(dateValue.end) : "",
			selectedRecipientId,
			{
				status: "active",
				sortBy: currentSortBy,
				sortDirection: currentSortDirection,
				pageIndex: 0,
				pageSize: EXPORT_MAX_ROWS,
				globalFilter,
				updateStore: false,
			}
		);
	};

	useEffect(() => {
		const isDateRangeValid =
			(dateValue.start && dateValue.end) ||
			(!dateValue.start && !dateValue.end);
		if (!isDateRangeValid) return;
		handleFetchAccountingData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dateValue, selectedRecipientId, sorting, pagination, globalFilter]);

	// Handle invoice cancellation with reason dialog
	const handleCancelInvoice = async (
		reason: string,
		rebookAsAkonto: boolean,
		cancelReminderFees: boolean
	) => {
		if (
			!selectedInvoiceForCancellation ||
			!selectedInvoiceForCancellation.invoice_id
		)
			return;

		setCancellationLoading(true);
		try {
			const invoiceId = selectedInvoiceForCancellation.invoice_id;
			await cancelInvoice(
				invoiceId,
				reason,
				rebookAsAkonto,
				cancelReminderFees
			);
			await handleFetchAccountingData();
		} catch (error) {
			Logger.error(error);
			showNotification({
				message: "Fehler beim Stornieren der Rechnung",
				type: "error",
			});
		} finally {
			setCancellationLoading(false);
		}
	};

	// Handle sorting changes - reset to page 1 when sorting changes
	const handleSortingChange = (
		updater: SortingState | ((old: SortingState) => SortingState)
	) => {
		setSorting(updater);
		setPagination((prev) => ({ ...prev, pageIndex: 0 }));
	};

	return (
		<div className="py-6 flex flex-col w-full items-center">
			<div className="w-full max-w-[1920px] px-6">
				{/* Toolbar */}
				<div className="flex items-center justify-between gap-4 py-3 mb-4 border-b border-gray-200">
					{/* Left: Filters */}
					<div className="flex items-center gap-6">
						<TooltipWrapper title="Die Ansicht zeigt alle gültigen Rechnungen inkl. Zahlungen.">
							<button
								type="button"
								className="text-gray-400 hover:text-gray-600 p-1"
								aria-label="Zweck anzeigen"
							>
								<Info className="h-4 w-4" />
							</button>
						</TooltipWrapper>
						<div className="flex items-center gap-2">
							<span className="text-sm text-gray-500 whitespace-nowrap">
								Zeitraum:
							</span>
							<CustomDateRangePicker
								dateVal={dateValue}
								handleDateChange={setDateValue}
							/>
							{(dateValue.start || dateValue.end) && (
								<button
									type="button"
									onClick={() =>
										setDateValue({ start: null, end: null })
									}
									className="text-gray-400 hover:text-gray-600"
									aria-label="Zeitraum löschen"
								>
									<X size={16} />
								</button>
							)}
						</div>
						<div className="h-8 w-px bg-gray-200" />
						<AccountingRecipientSelector
							selectedRecipientId={selectedRecipientId}
							onRecipientChange={(recipientId) => {
								setSelectedRecipientId(recipientId);
								setSearchParams({ recipient: recipientId });
								setPagination((prev) => ({
									...prev,
									pageIndex: 0,
								}));
							}}
							recipientList={augmentedRecipientsLookup}
						/>
						<div className="h-8 w-px bg-gray-200" />
						<div className="relative">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
							<input
								type="text"
								placeholder="Suchen..."
								value={globalFilter}
								onChange={(e) =>
									setGlobalFilter(e.target.value)
								}
								className="w-64 h-10 pl-10 pr-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
							/>
						</div>
					</div>
					{/* Right: Actions */}
					<div className="flex items-center gap-2">
						<TableExportDialog
							invoiceData={invoicePaymentsData}
							loading={loading}
							incompleteDateRange={
								Boolean(dateValue.start) !==
								Boolean(dateValue.end)
							}
							onExport={exportTable}
							fetchExportData={fetchExportData}
							currentSorting={
								sorting[0]
									? `${sorting[0].id} (${
											sorting[0].desc
												? "absteigend"
												: "aufsteigend"
										})`
									: "Rechnungsdatum (absteigend)"
							}
							headerMetadata={headerMetadata}
							showPaymentsOption={true}
							showInvoicePdfsOption={true}
						/>
						{isConfigTrue(organization?.config, "zak_enabled") && (
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant="secondary"
										onClick={() => setZakDialogOpen(true)}
									>
										An ZAK senden
									</Button>
								</TooltipTrigger>
								<TooltipContent>
									Rechnungen an ZAK senden
								</TooltipContent>
							</Tooltip>
						)}
						<Camt54Upload
							organizationId={organizationId ?? undefined}
							accountingData={invoicePaymentsData}
							onProcessingComplete={handleFetchAccountingData}
						/>
					</div>
				</div>

				<AbstractInvoiceTable<TableRowData>
					data={(() => {
						// Transform invoice data to include payment sub-rows
						if (
							!invoicePaymentsData ||
							!Array.isArray(invoicePaymentsData)
						) {
							return [];
						}
						return invoicePaymentsData
							.filter((invoice) => invoice != null)
							.map((invoice) => ({
								...invoice,
								isPaymentRow: false as const,
								subRows: (
									invoice.payments as unknown as PaymentDetail[]
								)?.map((payment: PaymentDetail) => ({
									...payment,
									invoice_id: invoice.invoice_id || 0,
									isPaymentRow: true as const,
								})),
							}));
					})()}
					tableType="active"
					loading={loading}
					features={{
						allowSharing: true,
						allowCancelInvoice: true,
						allowPayments: true,
						allowBookAsLoss: true,
						readOnlyMode: false,
						historicalView: false,
						allowZak: isConfigTrue(
							organization?.config,
							"zak_enabled"
						),
					}}
					uiCallbacks={{
						onPaymentDialogOpen: (invoice) => {
							setSelectedInvoiceForPayment(invoice);
							setPaymentDialogOpen(true);
						},
						onCancellationDialogOpen: (invoice) => {
							setSelectedInvoiceForCancellation(invoice);
							setCancellationDialogOpen(true);
						},
						onRefreshData: handleFetchAccountingData,
						reminderActions,
					}}
					// Table state
					sorting={sorting}
					onSortingChange={handleSortingChange}
					pagination={pagination}
					onPaginationChange={setPagination}
					globalFilter={globalFilter}
					onGlobalFilterChange={setGlobalFilter}
					// Data fetching
					totalRowCount={totalRowCount}
					manualSorting={true}
					manualPagination={true}
					manualFiltering={true}
					showVATSummary={true}
					emptyStateLabel="Keine Rechnungen vorhanden"
				/>
			</div>

			<AccountingZakDialog
				open={zakDialogOpen}
				onOpenChange={setZakDialogOpen}
				accountingData={invoicePaymentsData}
				onZakSent={async (invoiceIds: number[]) => {
					await handleFetchAccountingData();

					// Get fresh data from store after fetch
					const freshInvoiceData =
						useAccountingStore.getState().invoicePaymentsData;

					// Build ProcessedTransaction objects for ZAK sent invoices
					const zakTransactions: ProcessedTransaction[] =
						invoiceIds.map((invoiceId) => {
							const invoice = freshInvoiceData.find(
								(inv) => inv.invoice_id === invoiceId
							);

							if (invoice) {
								// Get client display name from invoice
								let clientName = "Unbekannt";
								if (invoice.recipient_is_company) {
									clientName =
										invoice.recipient_company_name ||
										"Unbekannt";
								} else if (
									invoice.recipient_first_name ||
									invoice.recipient_last_name
								) {
									clientName = `${
										invoice.recipient_first_name || ""
									} ${
										invoice.recipient_last_name || ""
									}`.trim();
								}

								return {
									invoice_id: invoiceId,
									invoice_doc_number:
										invoice.invoice_doc_number || null,
									invoice_date: invoice.invoice_date || null,
									client_name: clientName,
									amount: invoice.invoice_value || 0,
									currency: "CHF",
									success: true,
								};
							}

							// Fallback for invoices not found in state
							return {
								invoice_id: invoiceId,
								invoice_doc_number: null,
								invoice_date: null,
								client_name: null,
								amount: 0,
								currency: "CHF",
								success: false,
								error: "Rechnung nicht gefunden",
							};
						});

					setZakSentTransactions(zakTransactions);
					setZakResultsDialogOpen(true);
				}}
			/>
			<PaymentDialog
				open={paymentDialogOpen}
				onClose={() => {
					setPaymentDialogOpen(false);
					setSelectedInvoiceForPayment(null);
				}}
				invoice={selectedInvoiceForPayment}
				onSubmit={async (paymentData) => {
					await createPayment(paymentData);
					setPaymentDialogOpen(false);
					setSelectedInvoiceForPayment(null);
					await handleFetchAccountingData();
				}}
				loading={false}
			/>

			<InvoiceCancellationDialog
				isOpen={cancellationDialogOpen}
				onClose={() => {
					setCancellationDialogOpen(false);
					setSelectedInvoiceForCancellation(null);
					setCancellationLoading(false);
				}}
				onConfirm={handleCancelInvoice}
				invoice={selectedInvoiceForCancellation}
				loading={cancellationLoading}
			/>

			<ProcessingResultsDialog
				open={zakResultsDialogOpen}
				onClose={() => setZakResultsDialogOpen(false)}
				transactions={zakSentTransactions}
				mode="zak"
			/>

			{reminderDialogs}
		</div>
	);
};
